import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, InputText, SectionCard, TextArea, PageWrapper } from "@/deps/ui-library";
import { api } from "@/api";
import { useAccounts, useBillingItems, useInvoiceSetting, useSaveQuotation } from "@/api/queries";
import type { Party, Quotation } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { LineEditor, type EditableLine } from "@/components/LineEditor/LineEditor";
import { PageHeader } from "@/components/PageHeader";
import { PartyCombobox } from "@/components/PartyCombobox";
import { TotalsBlock, VatBreakdownTable } from "@/components/TotalsBlock";
import {
  DocumentAdjustments,
  adjustmentIsComplete,
  type EditableAdjustment,
} from "@/features/sales/invoices/DocumentAdjustments";
import { usePartyFromAddress } from "@/features/parties/usePartyFromAddress";
import { calculateTotals } from "@/lib/invoiceTotals";
import { sellingAccounts } from "@/lib/sellingAccounts";
import { DateField } from "@/components/DateField";

const today = () => new Date().toISOString().slice(0, 10);

/** Thirty days on, which is what the design starts a quotation with. */
const monthFrom = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + 30);
  return day.toISOString().slice(0, 10);
};

const STARTING_TERMS = "Prices hold until the validity date. Work begins on written acceptance.";

/**
 * Writes a quotation (ADR-0054). It saves a draft, and sending it is a step on the quotation itself.
 * It has an invoice's lines, so the invoice it becomes has the same figures.
 *
 * Given a draft and its customer, it edits that draft.
 */
export function QuotationEditorPage({
  editing,
}: { editing?: { quotation: Quotation; customer: Party } } = {}) {
  const say = useTaxWords();
  const navigate = useNavigate();
  const { data: chart } = useAccounts();
  const revenueAccounts = sellingAccounts(chart);
  const { data: billingItems } = useBillingItems({});
  const { data: setting } = useInvoiceSetting();
  const save = useSaveQuotation();

  const draft = editing?.quotation;
  const back = draft
    ? ({ to: "/sales/quotations/$id", params: { id: draft.id } } as const)
    : ({ to: "/sales/quotations" } as const);

  const [customer, setCustomer] = useState<Party | null>(editing?.customer ?? null);
  usePartyFromAddress("customer", setCustomer);
  const [date, setDate] = useState(draft?.date ?? today());
  const [typedValid, setTypedValid] = useState<string | null>(draft?.validUntil ?? null);
  const validUntil = typedValid ?? (date ? monthFrom(date) : "");
  const [reference, setReference] = useState(draft?.reference ?? "");
  const [terms, setTerms] = useState(draft ? (draft.terms ?? "") : STARTING_TERMS);
  const [note, setNote] = useState(draft?.note ?? "");
  const [lines, setLines] = useState<EditableLine[]>(
    draft
      ? draft.lines.map((line) => ({
          id: String(line.n),
          description: line.itemName,
          accountId: line.accountId,
          category: line.vatCategory,
          rate: line.vatRatePct,
          grossPrice: line.unitPrice,
          quantity: line.quantity,
          itemCode: line.itemCode,
          exemptionReason: line.exemptionReason,
          billingItemId: line.billingItemId,
          charges: line.charges,
          allowances: line.allowances,
        }))
      : [
          {
            id: "1",
            description: "",
            accountId: "",
            category: "S",
            rate: "13",
            grossPrice: "",
            quantity: "1",
          },
        ],
  );
  const withAccounts = lines.map((line) =>
    line.accountId === "" ? { ...line, accountId: revenueAccounts[0]?.id ?? "" } : line,
  );
  const [adjustments, setAdjustments] = useState<EditableAdjustment[]>(
    (draft?.adjustments ?? []).map((adjustment) => ({ ...adjustment, id: crypto.randomUUID() })),
  );
  const totals = calculateTotals(lines, adjustments);

  const datesWrong = date !== "" && validUntil !== "" && validUntil < date;
  const canSave =
    customer !== null &&
    date !== "" &&
    validUntil !== "" &&
    !datesWrong &&
    Number(totals.totalWithVat) > 0 &&
    withAccounts.every((line) => line.accountId !== "" && line.description.trim() !== "") &&
    adjustments.every(adjustmentIsComplete) &&
    !save.isPending;

  function submit() {
    save.mutate(
      {
        id: draft?.id,
        customerId: customer!.id,
        date,
        validUntil,
        reference: reference.trim() || undefined,
        terms: terms.trim() || undefined,
        note: note.trim() || undefined,
        lines: withAccounts.map((line) => ({
          description: line.description,
          quantity: line.quantity,
          unitPrice: line.grossPrice,
          vatCategory: line.category,
          ratePct: line.rate ?? "0",
          accountId: line.accountId,
          itemCode: line.itemCode,
          exemptionReason: line.exemptionReason,
          billingItemId: line.billingItemId,
          charges: line.charges,
          allowances: line.allowances,
        })),
        adjustments: adjustments.map(({ id: _id, ...adjustment }) => adjustment),
      },
      {
        onSuccess: (saved) =>
          void navigate({ to: "/sales/quotations/$id", params: { id: saved.id } }),
      },
    );
  }

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title={draft ? "Edit quotation" : "New quotation"}
        actions={
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              onClick={() => void navigate(back)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!canSave}
              onClick={submit}
            >
              {save.isPending ? "Saving" : "Save draft"}
            </Button>
          </span>
        }
      />

      {save.isError ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={
            save.error instanceof Error ? save.error.message : "That quotation could not be saved."
          }
        />
      ) : null}

      <SectionCard title="Quotation">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <PartyCombobox
            label="Customer"
            required
            value={customer}
            onChange={setCustomer}
            search={(q) => api.parties.search("customer", q)}
          />
          <InputText
            label="Customer reference"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
          <DateField label="Quotation date" required value={date} onChange={setDate} />
          <DateField
            label="Valid until"
            required
            value={validUntil}
            onChange={setTypedValid}
            helperText={datesWrong ? "This is before the quotation date." : undefined}
          />
        </div>
      </SectionCard>

      <SectionCard title="Lines">
        <LineEditor
          lines={withAccounts}
          onChange={setLines}
          accounts={revenueAccounts}
          items={billingItems}
          showHsCode={setting?.showHsCode ?? false}
          lineDiscounts={
            (setting?.lineDiscounts ?? false) ||
            withAccounts.some((line) => line.allowances || line.charges)
          }
        />
      </SectionCard>

      <SectionCard title="Discounts and charges on the quotation">
        <DocumentAdjustments
          adjustments={adjustments}
          onChange={setAdjustments}
          allowDiscounts={
            (setting?.documentDiscounts ?? false) || adjustments.some((a) => !a.isCharge)
          }
        />
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-2">
        <SectionCard title="Terms and note">
          <div className="flex flex-col gap-3">
            <TextArea
              label="Terms"
              rows={3}
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
            />
            <TextArea
              label="Note"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </SectionCard>
        <div className="flex flex-col gap-4">
          {setting?.showVatBreakdown ? (
            <SectionCard title={say("VAT breakdown")} flush>
              <VatBreakdownTable totals={totals} />
            </SectionCard>
          ) : null}
          <SectionCard title="Totals">
            <TotalsBlock totals={totals} showDue={false} />
          </SectionCard>
        </div>
      </div>
    </PageWrapper>
  );
}
